import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  TrunkCreateRequest,
  TrunkDetail,
  TrunkRow,
  TrunkUpdateRequest,
} from './types';

export const trunkKeys = {
  all: ['trunks'] as const,
  list: () => ['trunks', 'list'] as const,
  detail: (trunkId: string | number) => ['trunks', 'detail', trunkId] as const,
};

export function useTrunksList() {
  return useQuery({
    queryKey: trunkKeys.list(),
    queryFn: () => api.get<TrunkRow[]>('/api/obd/trunks/list'),
    staleTime: 30_000,
    select: (data) => ensureArray<TrunkRow>(data),
  });
}

export function useTrunkDetail(trunkId: string | number | null) {
  return useQuery({
    queryKey: trunkKeys.detail(trunkId ?? 'none'),
    queryFn: () => api.get<TrunkDetail>(`/api/obd/trunk/detail/${trunkId}`),
    enabled: Boolean(trunkId),
    staleTime: 30_000,
  });
}

function useInvalidateTrunks() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: trunkKeys.all });
}

export function useCreateTrunk() {
  const invalidate = useInvalidateTrunks();
  return useMutation({
    mutationFn: (req: TrunkCreateRequest) =>
      api.post<{ message?: string }>('/api/obd/trunk', req),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateTrunk() {
  const invalidate = useInvalidateTrunks();
  return useMutation({
    mutationFn: (req: TrunkUpdateRequest) =>
      api.post<{ message?: string }>('/api/obd/trunk/update', req),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteTrunk() {
  const invalidate = useInvalidateTrunks();
  return useMutation({
    mutationFn: (trunkId: number) =>
      api.delete<{ message?: string }>(`/api/obd/trunk/${trunkId}`),
    onSuccess: () => invalidate(),
  });
}
